import { GetApi, Api } from "../../../api/api"
import { FCCustomizationImportProgress, ProcessStatus } from "../../../api/types"

export interface FccImportProgress {
  fileName: string
  progress: number
  counters: Array<{
    caption: string
    count: number
  }>
  status: ProcessStatus
  message: string | null
  errors: string[]
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms)) // sleep helper function

const importFccFromExcel = async (
  fccId: string,
  inputFile: any,
  progressCallback: (progress: FccImportProgress | undefined) => void
) => {
  console.log("importing file to FCC", inputFile, fccId)
  progressCallback({
    fileName: inputFile.name,
    counters: [],
    progress: 0,
    status: ProcessStatus.Running,
    message: "Reading file...",
    errors: []
  })
  let ticketId: string
  const api = await GetApi()
  try {
    const ticket = await api.importFeatureCatalogCustomizationFromExcel(fccId, inputFile)
    progressCallback(ticketToProgress(ticket, inputFile.name))
    ticketId = ticket.id!
  }
  catch (error) {
    console.error("Error importing file to FCC", error)
    progressCallback(undefined)
    throw error
  }
  const status = await pollImportStatus(api, fccId, ticketId, inputFile.name, progressCallback)
  switch (status) {
    case ProcessStatus.Completed:
      return
    case ProcessStatus.Failed:
      throw new Error("Feature catalog import failed")
    case ProcessStatus.Canceled:
      throw new Error("Feature catalog import canceled")
    case ProcessStatus.Running:
      throw new Error("Timed out while waiting for feature catalog import to finish")
  }
}

const ticketToProgress = (ticket: FCCustomizationImportProgress, fileName: string): FccImportProgress => {
  let msg: string = ""
  switch (ticket.status) {
    case ProcessStatus.Running:
      msg = ticket.timeSpent === 0 ? "Validating..." : "Importing..."
      break
    case ProcessStatus.Completed:
      msg = "Import completed successfully"
      break
    case ProcessStatus.Failed:
      msg = "Import failed"
      break
    case ProcessStatus.Canceled:
      msg = "Import canceled"
      break
  }
  return {
    fileName,
    counters: [
      { caption: "Feature types", count: ticket.featureTypeCount ?? 0 },
      { caption: "Property sets", count: ticket.pSetCount ?? 0 },
      { caption: "Properties", count: ticket.propertyCount ?? 0 },
      { caption: "Feature types deleted ", count: ticket.deletedFeatureTypeCount ?? 0 },
      { caption: "Property sets deleted", count: ticket.deletedPSetCount ?? 0 },
      { caption: "Properties deleted", count: ticket.deletedPropertyCount ?? 0 }
    ],
    progress: ticket.progress ?? 0,
    status: ticket.status ?? ProcessStatus.Running,
    message: msg,
    errors: ticket.errors?.map(e => `${e.key}: ${e.message}`) ?? []
  }
}

const pollImportStatus = async (
  api: Api,
  fccId: string,
  ticketId: string,
  fileName: string,
  progressCallback: (progress: FccImportProgress | undefined) => void
): Promise<ProcessStatus> => {
  const importTimeout = 3600000 // One hour in milliseconds
  const pollInterval = 2000 // Two seconds in milliseconds

  let pollTime = 0;
  while (pollTime < importTimeout) {
    await sleep(pollInterval)
    try {
      const ticket = await api.getFccImportProgress(fccId, ticketId)
      progressCallback(ticketToProgress(ticket, fileName))
      if (ticket.status !== ProcessStatus.Running) {
        return ticket.status ?? ProcessStatus.Failed
      }
    }
    catch (error) {
      console.error("Error polling import status", error)
    }
    pollTime = pollTime + pollInterval;
  }
  progressCallback(undefined)
  return ProcessStatus.Running
}


export default importFccFromExcel;